import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Background from "../components/Background";
import AyupathCard from "../components/AyupathCard";
import Field from "../components/Field";
import Select from "../components/Select";
import PrimaryButton from "../components/PrimaryButton";
import SecondaryButton from "../components/SecondaryButton";
import { track } from "../utils/analytics";
import { correctBirthTime } from "../services/teaserApi";

const WHERE_TO_LOOK = [
  ["Your birth certificate", "In Australia the full certificate usually shows time of birth. The short extract often doesn't."],
  ["Births, Deaths and Marriages", "Your state's registry can issue a full certificate if you don't have one."],
  ["Hospital records or baby book", "Discharge papers and baby books often record the time."],
];

export default function UpdateBirthTime() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get("requestId") || "";

  const [hour, setHour] = useState("");
  const [minute, setMinute] = useState("");
  const [meridiem, setMeridiem] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const hours = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), []);
  const minutes = useMemo(() => Array.from({ length: 60 }, (_, i) => i), []);
  const canSubmit = hour !== "" && minute !== "" && meridiem !== "" && !submitting;

  async function submit() {
    if (!canSubmit) return;
    setError("");
    setSubmitting(true);
    try {
      let hour24 = Number(hour) % 12;
      if (meridiem === "PM") hour24 += 12;
      const time = `${String(hour24).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;

      await correctBirthTime(requestId, time);
      track("birth_time_corrected", { requestId });
      navigate(`/success?requestId=${encodeURIComponent(requestId)}`, { replace: true });
    } catch (err) {
      setError(err.message || "We couldn't update your birth time. Please try again.");
      setSubmitting(false);
    }
  }

  if (!requestId) {
    return (
      <Background>
        <div className="min-h-screen px-4 py-10 flex flex-col items-center">
          <div className="w-full max-w-md mx-auto">
            <AyupathCard>
              <div className="space-y-4 text-center">
                <h2 className="text-2xl font-semibold text-gray-900">We need your reading link</h2>
                <p className="text-gray-600">
                  Open the link from your preview email, then come back to this page.
                </p>
                <SecondaryButton onClick={() => navigate("/")}>Back to Your Next Chapter</SecondaryButton>
              </div>
            </AyupathCard>
          </div>
        </div>
      </Background>
    );
  }

  return (
    <Background>
      <div className="min-h-screen px-4 py-10 flex flex-col items-center">
        <div className="w-full max-w-md mx-auto space-y-6">
          <AyupathCard>
            <div className="space-y-5">
              <div>
                <h2 className="text-2xl font-semibold text-gray-900">Found your exact time?</h2>
                <p className="text-gray-600 mt-1">
                  Add it here and we'll redo your reading properly — including the parts
                  we had to leave out.
                </p>
              </div>

              <div className="rounded-2xl bg-orange-50 border border-orange-100 p-4">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold text-gray-900">Why it has to be the minute.</span>{" "}
                  One minute out moves your timeline by about five days. Fifteen minutes
                  moves it by two and a half months. That's the difference between a year
                  that means something and a year that's a guess.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <Field label="Hour">
                  <Select value={hour} onChange={(e) => setHour(Number(e.target.value))}>
                    <option value="">--</option>
                    {hours.map((h) => (
                      <option key={h} value={h}>{String(h).padStart(2, "0")}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="Minute">
                  <Select value={minute} onChange={(e) => setMinute(Number(e.target.value))}>
                    <option value="">--</option>
                    {minutes.map((m) => (
                      <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="AM/PM">
                  <Select value={meridiem} onChange={(e) => setMeridiem(e.target.value)}>
                    <option value="">--</option>
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </Select>
                </Field>
              </div>

              {error ? <p className="text-sm text-rose-600">{error}</p> : null}

              <PrimaryButton onClick={submit} disabled={!canSubmit}>
                {submitting ? "Redoing your reading…" : "Update and redo my reading"}
              </PrimaryButton>
            </div>
          </AyupathCard>

          <AyupathCard>
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900">Where to find it</h3>
              <ul className="space-y-3">
                {WHERE_TO_LOOK.map(([title, detail]) => (
                  <li key={title}>
                    <div className="text-sm font-semibold text-gray-900">{title}</div>
                    <div className="text-sm text-gray-600">{detail}</div>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-gray-600">
                No rush — your reading stays here. Come back whenever you have it.
              </p>
            </div>
          </AyupathCard>
        </div>
      </div>
    </Background>
  );
}
